// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Grid, Typography } from '@mui/material';
import rfdc from 'rfdc';
import {
  GenericEnumInput,
  GenericMultiSelectWithSelectAll,
  GenericNumberInput,
  GenericTextInput,
  GenericDateInput,
  ItemList,
} from '../../../../../../components/ScenarioParameters/components/ScenarioParametersInputs';
import { useOrganizationId } from '../../../../../../state/hooks/OrganizationHooks.js';
import { useWorkspaceId } from '../../../../../../state/hooks/WorkspaceHooks.js';
import { UploadFile, BasicEnumInput } from '../../../../../../ui';
import { ConfigUtils, SolutionsUtils, TranslationUtils } from '../../../../../../utils';
import { FileManagementUtils } from '../../../../../../utils/FileManagementUtils';
import { useDatasetCreationParameters } from './DatasetCreationParametersHook';

const clone = rfdc();

export const DatasetCreationParameters = ({ dataSourceRunTemplates, parentDataset, selectedRunner }) => {
  const { t } = useTranslation();
  const { getValues, resetField } = useFormContext();
  const organizationId = useOrganizationId();
  const workspaceId = useWorkspaceId();
  const { datasourceParameterHelpers, getDataSourceTypeEnumValues, getUploadFileLabels, getDefaultFileTypeFilter } =
    useDatasetCreationParameters();

  const isSubDatasetCreationWizard = useMemo(() => parentDataset != null, [parentDataset]);
  const isDatasetParametersEditionDialog = selectedRunner && Object.keys(selectedRunner).length > 0;
  const [dataSourceType, setDataSourceType] = useState(selectedRunner?.runTemplateId ?? null);
  const dataSourceTypeEnumValues = useMemo(
    () => getDataSourceTypeEnumValues(dataSourceRunTemplates),
    [getDataSourceTypeEnumValues, dataSourceRunTemplates]
  );
  const defaultDataSourceTypeKey = useMemo(() => dataSourceTypeEnumValues?.[0]?.key ?? '', [dataSourceTypeEnumValues]);
  const selectedRunnerDataSourceLabel = useMemo(() => {
    return dataSourceTypeEnumValues?.find((rt) => rt.key === dataSourceType)?.value || dataSourceType;
  }, [dataSourceTypeEnumValues, dataSourceType]);

  useEffect(() => {
    if (dataSourceType == null) setDataSourceType(defaultDataSourceTypeKey);
  }, [dataSourceType, setDataSourceType, defaultDataSourceTypeKey]);

  const defaultFormState = useRef({});
  const sourceParameters = useMemo(() => {
    const forgeParameterInput = (originalParameter) => {
      const parameterId = originalParameter.id;
      const datasourcePatch = datasourceParameterHelpers?.find((datasource) => datasource.id === dataSourceType);
      const parametersPatch = datasourcePatch?.parameters?.find((el) => el.id === parameterId);
      const parameter = clone(originalParameter);

      if (parametersPatch) {
        parameter.defaultValue = parametersPatch.defaultValue;
        parameter.options = parameter.options ?? {};
        parameter.options.tooltipText = parametersPatch.tooltipText;
      }
      const escapedSourceType = SolutionsUtils.escapeRunTemplateId(dataSourceType);
      const fieldPath = `${escapedSourceType}.${parameterId}`;
      const inputType = parameter.varType;
      let defaultValue;
      const isRequired =
        ConfigUtils.getParameterAttribute(parameter, 'required') || !['list', 'itemList'].includes(inputType);
      if (inputType === 'string') defaultValue = parameter?.defaultValue ?? '';
      else if (inputType === 'int' || inputType === 'number') defaultValue = parameter?.defaultValue;
      else if (inputType === 'enum') {
        const enumValues = ConfigUtils.getParameterAttribute(parameter, 'enumValues') ?? [];
        defaultValue = enumValues?.[0]?.key;
      } else if (inputType === 'list') {
        defaultValue = [];
      } else if (inputType === '%DATASETID%') {
        defaultValue = null;
      } else if (inputType === 'date') {
        defaultValue = new Date();
      } else if (inputType === 'itemList') {
        defaultValue = [];
      } else {
        console.error(`VarType "${inputType}" is not supported for ETL runner parameters.`);
        return null;
      }
      defaultFormState.current[fieldPath] = defaultValue;

      // TODO: replace by generic webapp code when int & number varTypes are supported as dataset creation parameters
      let customConstraints = {};
      if (inputType === 'int' || inputType === 'number') {
        customConstraints = {
          min: {
            value: parameter?.minValue,
            message: t(
              'views.scenario.scenarioParametersValidationErrors.minValue',
              'Minimum value for this field is {{minValue}}',
              { minValue: parameter?.minValue }
            ),
          },
          max: {
            value: parameter?.maxValue,
            message: t(
              'views.scenario.scenarioParametersValidationErrors.maxValue',
              'Maximum value for this field is {{maxValue}}',
              { maxValue: parameter?.maxValue }
            ),
          },
          validate: {
            integer: (v) => {
              if (inputType === 'int') {
                return (
                  Number.isInteger(v) ||
                  t('views.scenario.scenarioParametersValidationErrors.integer', 'This value must be an integer')
                );
              }
            },
          },
        };
      }

      return (
        <Controller
          key={fieldPath}
          name={fieldPath}
          defaultValue={defaultValue}
          // TEMPORARY: Required is set to false for list and itemList parameters
          //  while this behavior is not supported by the generic web app
          // Note for future : in case of conflic with the generic web app,
          //  please accept current changes from the generic web app
          rules={{
            required: isRequired,
            ...customConstraints,
          }}
          render={({ field, fieldState }) => {
            const { value, onChange } = field;
            const { error } = fieldState;
            if (inputType === 'string') {
              return (
                <GenericTextInput
                  parameterData={parameter}
                  context={{ editMode: true }}
                  parameterValue={value}
                  setParameterValue={onChange}
                  gridItemProps={{ xs: 12, sx: { pt: 1 } }}
                  size="medium"
                  isDirty={null}
                />
              );
            } else if (inputType === 'int' || inputType === 'number') {
              return (
                <GenericNumberInput
                  parameterData={parameter}
                  context={{ editMode: true }}
                  parameterValue={value}
                  setParameterValue={onChange}
                  resetParameterValue={(newDefaultValue) => resetField(fieldPath, { defaultValue: newDefaultValue })}
                  isDirty={null}
                  gridItemProps={{ xs: 12, sx: { pt: 2 } }}
                  error={error}
                />
              );
            } else if (inputType === 'enum') {
              return (
                <GenericEnumInput
                  parameterData={parameter}
                  context={{ editMode: true, targetDatasetId: parentDataset?.id }}
                  parameterValue={value}
                  setParameterValue={onChange}
                  resetParameterValue={(newDefaultValue) => resetField(fieldPath, { defaultValue: newDefaultValue })}
                  gridItemProps={{ xs: 6, sx: { pt: 2 } }}
                  isDirty={null}
                />
              );
            } else if (inputType === 'list') {
              return (
                <GenericMultiSelectWithSelectAll
                  gridItemProps={{ xs: 12, sx: { pt: 2 } }}
                  parameterData={parameter}
                  context={{ editMode: true, targetDatasetId: parentDataset?.id }}
                  parameterValue={value}
                  setParameterValue={onChange}
                  isDirty={null}
                  error={error}
                />
              );
            } else if (inputType === '%DATASETID%') {
              return (
                <Grid item xs={12} sx={{ pt: 1 }}>
                  <UploadFile
                    id={parameterId}
                    key={parameterId}
                    labels={getUploadFileLabels(parameterId, parameter.idForTranslationKey)}
                    tooltipText={t(TranslationUtils.getParameterTooltipTranslationKey(parameterId), '')}
                    handleUploadFile={(event) => FileManagementUtils.prepareToUpload(event, onChange)}
                    handleDownloadFile={(event) => {
                      FileManagementUtils.downloadFile(organizationId, workspaceId, value.id, () => {});
                    }}
                    editMode={true}
                    handleDeleteFile={() => onChange(null)}
                    file={value ?? {}}
                    acceptedFileTypes={getDefaultFileTypeFilter(dataSourceRunTemplates, parameterId)}
                  />
                </Grid>
              );
            } else if (inputType === 'date') {
              return (
                <Grid container columns={6} sx={{ pt: 1 }}>
                  <GenericDateInput
                    parameterData={parameter}
                    context={{ editMode: true }}
                    parameterValue={value}
                    setParameterValue={onChange}
                    isDirty={null}
                    error={null}
                  />
                </Grid>
              );
            } else if (inputType === 'itemList') {
              return (
                <ItemList
                  parameterData={parameter}
                  context={{ editMode: true, targetDatasetId: parentDataset?.id }}
                  parameterValue={value}
                  setParameterValue={onChange}
                  error={error}
                  gridItemProps={{ xs: 12, sx: { pt: 1 } }}
                />
              );
            } else {
              return null;
            }
          }}
        />
      );
    };

    const runTemplate = dataSourceRunTemplates[dataSourceType];
    defaultFormState.current = {};
    return runTemplate?.parameters?.map((parameter) => forgeParameterInput(parameter));
  }, [
    organizationId,
    workspaceId,
    dataSourceRunTemplates,
    dataSourceType,
    datasourceParameterHelpers,
    parentDataset?.id,
    resetField,
    getUploadFileLabels,
    t,
    getDefaultFileTypeFilter,
  ]);

  useEffect(() => {
    // Do not reset form when updating an existing dataset (already done in a useEffect in UpdateDatasetDialog)
    if (isDatasetParametersEditionDialog) return;

    const formValues = getValues();
    for (const [sourceType, parameters] of Object.entries(formValues)) {
      // Ignore fields that are not source types
      if (['name', 'tags', 'description', 'sourceType'].includes(sourceType)) continue;
      for (const [parameterId, parameterValue] of Object.entries(parameters)) {
        const escapedSourceType = SolutionsUtils.escapeRunTemplateId(sourceType);
        const fieldPath = `${escapedSourceType}.${parameterId}`;
        defaultFormState.current[fieldPath] = parameterValue;
      }
    }
    for (const [key, defaultValue] of Object.entries(defaultFormState.current)) {
      resetField(key, { defaultValue });
    }
  }, [getValues, isDatasetParametersEditionDialog, resetField, sourceParameters]);

  const labels = useMemo(() => {
    return isSubDatasetCreationWizard
      ? {
          subtitle: t(
            'commoncomponents.datasetmanager.wizard.secondScreen.subdatasetSubtitle',
            'Please select a filter'
          ),
          sourceSelectLabel: t(
            'commoncomponents.datasetmanager.wizard.secondScreen.dataSourceType.subdatasetLabel',
            'Filter'
          ),
        }
      : {
          subtitle: t(
            'commoncomponents.datasetmanager.wizard.secondScreen.subtitle',
            'Please provide your data source'
          ),
          sourceSelectLabel: t('commoncomponents.datasetmanager.wizard.secondScreen.dataSourceType.label', 'Source'),
        };
  }, [t, isSubDatasetCreationWizard]);

  const sourceTypeComponent = isDatasetParametersEditionDialog ? (
    <Typography data-cy="selected-runner-source-type">
      {labels.sourceSelectLabel + ': ' + selectedRunnerDataSourceLabel}
    </Typography>
  ) : (
    <Controller
      name="sourceType"
      key="sourceType"
      defaultValue={dataSourceType ?? defaultDataSourceTypeKey}
      shouldUnregister={true}
      render={({ field }) => {
        const { value, onChange } = field;
        const setDatasetSource = (newValue) => {
          onChange(newValue);
          setDataSourceType(newValue);
        };

        return (
          <BasicEnumInput
            id="new-dataset-sourceType"
            label={labels.sourceSelectLabel}
            size="medium"
            value={value ?? defaultDataSourceTypeKey}
            changeEnumField={setDatasetSource}
            enumValues={dataSourceTypeEnumValues}
          />
        );
      }}
    />
  );

  return (
    <>
      {!isDatasetParametersEditionDialog && (
        <Grid item xs={12}>
          {<Typography sx={{ py: 2 }}>{labels.subtitle}</Typography>}
        </Grid>
      )}
      <Grid item xs={7}>
        {sourceTypeComponent}
      </Grid>
      <Grid item container xs={12} sx={{ px: 2, pt: 3 }}>
        {sourceParameters}
      </Grid>
    </>
  );
};

DatasetCreationParameters.propTypes = {
  dataSourceRunTemplates: PropTypes.object.isRequired,
  parentDataset: PropTypes.object,
  selectedRunner: PropTypes.object,
};
DatasetCreationParameters.defaultProps = {
  selectedRunner: {},
};
